export interface StarterPrompt {
  id: string;
  text: string;
  title?: string;
  description?: string;
  icon?: 'knowledge' | 'research' | 'files' | 'meetings' | 'skills' | 'agents' | 'results' | 'chat';
  connections?: { id: string; name: string }[];
}

export type StarterPromptKind = 'chat' | 'skill';

/** Starting points follow the conversation's own intent: creating a NEW Skill
 * asks for Skills worth adding, while ordinary chat — and refining a Skill that
 * already exists, where the conversation carries its own goal — keeps the
 * company's starter questions. */
export function starterPromptKind(intent?: { mode?: string; skillId?: string }): StarterPromptKind {
  return intent?.mode === 'skill-author' && !intent.skillId ? 'skill' : 'chat';
}

interface FetchStarterPromptsOptions {
  endpoint: string;
  headers?: HeadersInit;
  fetcher?: typeof fetch;
  signal?: AbortSignal;
}

function normalizeStarterPrompts(value: unknown): StarterPrompt[] {
  const prompts = (value as { prompts?: unknown })?.prompts;
  if (!Array.isArray(prompts)) return [];
  return prompts.flatMap((candidate) => {
    const prompt = candidate as Partial<StarterPrompt>;
    const id = typeof prompt.id === 'string' ? prompt.id.trim() : '';
    const text = typeof prompt.text === 'string' ? prompt.text.trim() : '';
    if (!id || !text) return [];
    return [
      {
        id,
        text,
        ...(typeof prompt.title === 'string' ? { title: prompt.title } : {}),
        ...(typeof prompt.description === 'string' ? { description: prompt.description } : {}),
        ...(prompt.icon &&
        [
          'knowledge',
          'research',
          'files',
          'meetings',
          'skills',
          'agents',
          'results',
          'chat',
        ].includes(prompt.icon)
          ? { icon: prompt.icon }
          : {}),
        ...(Array.isArray(prompt.connections)
          ? {
              connections: prompt.connections.filter(
                (item) => item && typeof item.id === 'string' && typeof item.name === 'string',
              ),
            }
          : {}),
      },
    ];
  });
}

/** Why new suggestions are paused: the company's plan, credits or this member's limit (D74 billing). */
export type StarterPause = {
  reason: 'payment_required';
  detail: 'plan_required' | 'credits_exhausted' | 'member_limit_reached' | null;
};
export type StarterResult = {
  /** Null when the read brought no set (it failed, or billing refused it): the shown cards stay. */
  prompts: StarterPrompt[] | null;
  refreshing: boolean;
  pause: StarterPause | null;
};

const PAUSE_DETAILS: readonly string[] = [
  'plan_required',
  'credits_exhausted',
  'member_limit_reached',
];

const pauseDetail = (value: unknown) =>
  typeof value === 'string' && PAUSE_DETAILS.includes(value)
    ? (value as StarterPause['detail'])
    : null;

/** Only the server's own payment_required answer (or a 402) pauses suggestions; a network or other error never
 * does. The detail is the billing block reason, in `detail` or, on a 402, `reason`. */
function pauseFrom(body: unknown, refused = false): StarterPause | null {
  const value = body as { reason?: unknown; code?: unknown; detail?: unknown } | null;
  if (!refused && value?.reason !== 'payment_required' && value?.code !== 'payment_required')
    return null;
  return {
    reason: 'payment_required',
    detail: pauseDetail(value?.detail) ?? pauseDetail(value?.reason),
  };
}

export async function fetchStarterResult({
  endpoint,
  headers = {},
  fetcher = fetch,
  signal,
}: FetchStarterPromptsOptions): Promise<StarterResult> {
  const failed = { prompts: null, refreshing: false, pause: null };
  try {
    const response = await fetcher(endpoint, {
      credentials: 'same-origin',
      headers,
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(120_000)])
        : AbortSignal.timeout(120_000),
    });
    if (response.status === 402)
      return { ...failed, pause: pauseFrom(await response.json().catch(() => null), true) };
    if (!response.ok) return failed;
    const body = await response.json();
    return {
      prompts: normalizeStarterPrompts(body),
      refreshing: body?.refreshing === true,
      pause: pauseFrom(body),
    };
  } catch {
    return failed;
  }
}

export async function fetchStarterPrompts(
  options: FetchStarterPromptsOptions,
): Promise<StarterPrompt[]> {
  return (await fetchStarterResult(options)).prompts ?? [];
}

// assistant-ui normalizes string suggestions to { title, prompt } with an
// empty label. Rendering and submission therefore share this exact value.
export function starterSuggestionTexts(prompts: readonly StarterPrompt[]): string[] {
  return prompts.map(({ text }) => text);
}

export function starterSuggestions(prompts: readonly StarterPrompt[]) {
  return prompts.map((prompt) =>
    prompt.title
      ? { title: prompt.title, label: prompt.description ?? '', prompt: prompt.text }
      : prompt.text,
  );
}
